import React, { useRef, useState } from 'react';
import styles from './ThreeDotMenu.module.scss';
import { isEmpty } from '../../utils/utils';
import clsx from 'clsx';
import { useOutsideClick } from '../../utils/constants/hooks';

type ThreeDotMenuProps = {
    menuItems: {
        name: React.ReactNode;
        onClick: Function;
        className?: string;
        keepOpenOnClick?: boolean;
        disabled?: boolean;
    }[];
    color?: React.CSSProperties['color'];
    tableName?: string;
    /** 'small' renders a compact 28px trigger (e.g. inside cards); default keeps the standard 40px */
    size?: 'default' | 'small';
};

const ThreeDotMenu = (props: ThreeDotMenuProps) => {
    const { menuItems, color, size } = props;
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const parentRef = useRef(null);

    const toggleMenu = () => {
        setIsOpen((prevState) => !prevState);
    };

    // One path for click and keyboard, so `disabled` and `keepOpenOnClick` cannot diverge between them.
    const activateItem = (item: ThreeDotMenuProps['menuItems'][number]) => {
        if (item.disabled) return;
        item.onClick();
        if (!item.keepOpenOnClick) setIsOpen(false);
    };

    useOutsideClick(parentRef, handleClickOutside);

    function handleClickOutside(event: MouseEvent) {
        setIsOpen(false);
    }

    if (isEmpty(props.menuItems)) return <></>;

    return (
        <div className={styles.threeDotMenu} ref={parentRef}>
            <button
                type="button"
                className={clsx(styles.icon, { [styles.iconSmall]: size === 'small' })}
                onClick={toggleMenu}
                style={{ color: color ?? undefined }}
                aria-label="Open actions menu"
                aria-haspopup="menu"
                aria-expanded={isOpen}
            >
                &#x22EE; {/* This is the vertical three-dot icon */}
            </button>
            {isOpen && (
                <div
                    className={clsx(
                        {
                            [styles.ManageEligibilityMenu]: props.tableName === 'ManageEligibilityTable',
                            [styles.CtaActionMenu]: props.tableName === 'CtaActionMenu',
                        },
                        styles.menu,
                    )}
                    ref={menuRef}
                >
                    <ul>
                        {menuItems.map((item, index) => (
                            <li
                                key={index}
                                // Focusable buttons rather than role="menu"/"menuitem": that role owes
                                // arrow-key navigation, a roving tabindex and focus management, and a
                                // half-implemented menu misleads assistive tech more than none. These are
                                // a list of actions, and Tab + Enter/Space is the honest contract.
                                role="button"
                                aria-disabled={item.disabled || undefined}
                                tabIndex={item.disabled ? undefined : 0}
                                onClick={() => activateItem(item)}
                                onKeyDown={(e) => {
                                    if (e.key !== 'Enter' && e.key !== ' ') return;
                                    e.preventDefault();
                                    activateItem(item);
                                }}
                                className={clsx({
                                    [styles.Delete]: item.className === 'Delete',
                                    [styles.disabled]: item.disabled,
                                })}
                            >
                                {item.name}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default ThreeDotMenu;
